import React, { useState, useRef, useEffect } from 'react';
import { isEscapeKey } from '../../../utils/keyboard';

interface InlineEditableTextareaProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  readOnlyClassName?: string;
  readOnly?: boolean;
}

const TextareaField: React.FC<InlineEditableTextareaProps> = ({
  id,
  value,
  onChange,
  placeholder = '',
  className = '',
  style = {},
  autoFocus = false,
  onKeyDown,
  onBlur,
  onFocus,
  readOnlyClassName = '',
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);

  // Sync local value with prop value when not editing
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value);
    }
  }, [value, isEditing]);

  // Auto focus when switching to edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  // Auto focus on mount if specified
  useEffect(() => {
    if (autoFocus && !readOnly) {
      setIsEditing(true);
    }
  }, [autoFocus, readOnly]);

  const handleClick = () => {
    if (!isEditing && !readOnly) {
      setIsEditing(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEscapeKey(e)) {
      e.preventDefault();
      setLocalValue(value);
      handleBlur();
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (onBlur) {
      onBlur();
    }
  };

  const handleFocus = () => {
    if (!readOnly) {
      setIsEditing(true);
    }
    if (onFocus) {
      onFocus();
    }
  };

  const isEmpty = !localValue || localValue.trim() === '';
  const showPlaceholder = isEmpty && !isEditing;

  if (isEditing) {
    return (
      <textarea
        id={id}
        ref={textareaRef}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={`outline-none resize-none w-full break-words flex-1 min-h-0 overflow-y-auto p-3 ${className}`}
        style={style}
      />
    );
  }

  return (
    <div
      ref={displayRef}
      onClick={handleClick}
      onFocus={handleFocus}
      tabIndex={0}
      className={`${readOnly ? 'cursor-default' : 'cursor-text'} break-words whitespace-pre-wrap flex-1 min-h-0 overflow-y-auto p-3 ${showPlaceholder ? 'text-gray-400' : ''} ${readOnlyClassName} ${className}`}
      style={style}
    >
      {showPlaceholder ? placeholder : localValue}
    </div>
  );
};

export default TextareaField;
